/*
 * Magnum PDP sticky add-to-cart bar.
 *
 * Design rule for this whole file: the bar MIRRORS the live DOM, it never owns
 * purchase state. Every control in it is a remote for the control already on
 * the product page (variant picker, subscription toggle + frequency select,
 * quantity input), and the bar's own button does nothing but forward a click to
 * the native submit button. So the selling plan, quantity rules, cart drawer,
 * the Klaviyo add-to-cart tracker (which reads form.elements), and the
 * checkout-button spinner all keep working exactly as they do without this bar.
 * Page -> bar sync is the same observer/event path the bar always used.
 */
if (!customElements.get('magnum-sticky-atc')) {
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const n = Number(cents) || 0;
    const withDelims = (num, precision, thousands, decimal) => {
      const parts = (num / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelims(n, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelims(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelims(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelims(n, 2, "'", '.');
        default:
          return withDelims(n, 2, ',', '.');
      }
    });
  };

  customElements.define(
    'magnum-sticky-atc',
    class MagnumStickyAtc extends HTMLElement {
      connectedCallback() {
        // 1. Re-entry guard. Re-parenting the node to <body> below re-fires
        //    connectedCallback; so does any later move.
        if (this.dataset.mounted) return;

        // 2. snippets/card-product.liquid renders main-product inside the
        //    quick-add modals. A second bar must never appear there.
        if (this.closest('quick-add-modal, .quick-add-modal')) {
          this.remove();
          return;
        }

        // 3. Singleton, newest wins. This is what keeps theme-editor section
        //    reloads clean: the fresh bar evicts the stale one.
        document.querySelectorAll('magnum-sticky-atc[data-mounted]').forEach((other) => {
          if (other !== this) other.remove();
        });

        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.nativeButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        // 4. Fail to NO bar rather than to a broken bar.
        if (!this.nativeButton || !this.productInfo) {
          this.remove();
          return;
        }

        this.imageEl = this.querySelector('[data-sticky-image]');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.buttonEl = this.querySelector('[data-sticky-button]');
        this.buttonTextEl = this.querySelector('[data-sticky-button-text]');
        this.spinnerEl = this.querySelector('.loading__spinner');
        this.initialImageSrc = this.imageEl ? this.imageEl.getAttribute('src') : null;

        this.selectEl = this.querySelector('[data-sticky-select]');
        this.planWrap = this.querySelector('[data-sticky-planwrap]');
        this.planInputs = Array.from(this.querySelectorAll('[data-sticky-plan]'));
        this.subInput = this.planInputs.find((i) => i.value === 'subscribe') || null;
        this.freqEl = this.querySelector('[data-sticky-freq]');
        this.freqOpts = Array.from(this.querySelectorAll('[data-sticky-freq-opt]'));
        this.qtyWrap = this.querySelector('[data-sticky-qty]');
        this.qtyInput = this.querySelector('[data-sticky-qty-input]');
        this.qtySteps = Array.from(this.querySelectorAll('[data-sticky-qty-step]'));
        if (this.freqEl) this.freqEl.inert = true;

        const data = this.parseData();
        this.moneyFormat = data.moneyFormat;
        this.variants = Array.isArray(data.variants) ? data.variants : [];
        this.hideVariantLine =
          this.variants.length <= 1 || (this.variants.length === 1 && this.variants[0].title === 'Default Title');

        this.syncQueued = false;
        this.teardownCalled = false;

        this.onClick = this.onClick.bind(this);
        this.onDomChange = this.onDomChange.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onSectionUnload = this.onSectionUnload.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.runSync = this.runSync.bind(this);
        this.onFreqOutside = this.onFreqOutside.bind(this);
        this.onFreqEsc = this.onFreqEsc.bind(this);

        this.dataset.mounted = 'true';

        // Mandatory: a transformed / filtered / contained ancestor would turn
        // position:fixed into position:absolute relative to that ancestor and
        // strand the bar mid-page. Only <body> is guaranteed safe.
        document.body.appendChild(this);

        this.bind();
        this.bindControls();
        this.requestSync();
      }

      parseData() {
        const node = this.querySelector('[data-sticky-data]');
        if (!node) return {};
        try {
          const parsed = JSON.parse(node.textContent);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (error) {
          return {};
        }
      }

      bind() {
        if (this.buttonEl) this.buttonEl.addEventListener('click', this.onClick);

        // Catch-all: product-info.js swaps innerHTML on variant change and
        // toggles the submit button's disabled attribute. childList also sees
        // the subscription toggle add/remove the form's selling_plan input,
        // which is how a plan change on the page reaches the bar.
        this.mutationObserver = new MutationObserver(this.onDomChange);
        this.mutationObserver.observe(this.productInfo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        // updateVariantInputs() dispatches a bubbling change on the hidden input;
        // the page quantity input fires change here too.
        this.productInfo.addEventListener('change', this.onDomChange);

        // The submit lifecycle lives on the native button as `class="loading"` +
        // aria-disabled (product-form.js sets both on submit and clears them in
        // its finally block). The subtree observer above filters to `disabled`
        // only -- widening it to `class` across all of product-info would fire on
        // every slider tick. One narrow observer on the button is cheap and exact.
        this.buttonStateObserver = new MutationObserver(this.onDomChange);
        this.buttonStateObserver.observe(this.nativeButton, {
          attributes: true,
          attributeFilter: ['class', 'aria-disabled', 'disabled'],
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onDomChange);
        }

        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);

        // Visibility: show only once the native button has been scrolled PAST.
        // rect.bottom < 0 is what distinguishes "above the viewport" from
        // "still below the fold", where the bar must stay hidden.
        //
        // An IntersectionObserver ALONE is not enough here. With threshold 0 it
        // only fires when isIntersecting flips. A scroll that jumps the button
        // straight from below the fold to above the viewport (anchor link,
        // restored scroll position, a fling on mobile) never intersects at all,
        // so no entry is delivered and the bar would stay hidden forever.
        // So the rAF-throttled scroll listener is the authority, and the
        // observer stays on to catch the button MOVING without a scroll
        // (variant swap, lazy image, accordion) which scroll alone would miss.
        this.intersectionObserver = new IntersectionObserver(this.updateVisibility, { threshold: 0 });
        this.intersectionObserver.observe(this.nativeButton);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // Publish the bar's height as --magnum-sticky-atc-h so the stylesheet can
        // lift the third-party accessibility widget clear of it. Measured rather
        // than hard-coded: the bar's height changes with viewport and font scale.
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => this.publishHeight());
          this.resizeObserver.observe(this);
        }
        this.publishHeight();

        this.updateVisibility();
      }

      /* ---------- page controls the bar drives (re-queried every time: product-info re-renders them) ---------- */

      productForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      pageToggle() {
        return this.productInfo.querySelector('.magnum-sub-toggle[data-has-plans="true"]');
      }

      pageFreq() {
        return document.getElementById(`magnum-sub-freq-${this.sectionId}`);
      }

      pageQty() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }

      pageMode() {
        const toggle = this.pageToggle();
        if (!toggle) return 'once';
        const selected = toggle.querySelector('.magnum-sub-toggle__option--selected');
        return selected && selected.dataset.mode === 'subscribe' ? 'subscribe' : 'once';
      }

      // The plan the cart will actually receive: the selling_plan input the page
      // toggle maintains in the product form (absent = one-time).
      activePlanId() {
        const form = this.productForm();
        const input = form && form.querySelector('[name="selling_plan"]');
        return input && input.value ? String(input.value) : null;
      }

      qtyLimits() {
        const page = this.pageQty();
        const min = page ? Math.max(1, parseInt(page.min, 10) || 1) : 1;
        const pageMax = page ? parseInt(page.max, 10) : NaN;
        const max = Number.isFinite(pageMax) && pageMax >= min ? pageMax : 99;
        const step = page ? Math.max(1, parseInt(page.step, 10) || 1) : 1;
        return { min, max, step };
      }

      currentQty() {
        const page = this.pageQty();
        const n = page ? parseInt(page.value, 10) : 1;
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      bindControls() {
        if (this.selectEl) {
          this.selectEl.addEventListener('change', () => this.setVariant(this.selectEl.value));
        }

        this.planInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            this.setPlan(input.value);
            if (input.value !== 'subscribe') this.closeFreq(false);
          });
          // Pointer click on SUBSCRIBE (even when already selected) opens the
          // frequency pop-up; keyboard users get it on Space/Enter.
          if (input === this.subInput) {
            input.addEventListener('click', (event) => {
              if (event.detail > 0) this.openFreq(false);
            });
            input.addEventListener('keydown', (event) => {
              if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
                event.preventDefault();
                if (!input.checked) {
                  input.checked = true;
                  this.setPlan('subscribe');
                }
                this.openFreq(true);
              }
            });
          }
        });

        this.freqOpts.forEach((opt) => {
          opt.addEventListener('change', () => {
            if (opt.checked) this.applyFreq(opt.value);
          });
          opt.addEventListener('click', (event) => {
            if (event.detail > 0) {
              this.applyFreq(opt.value);
              this.closeFreq(true);
            }
          });
          opt.addEventListener('keydown', (event) => {
            if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
              event.preventDefault();
              this.applyFreq(opt.value);
              this.closeFreq(true);
            }
          });
        });
        if (this.freqEl) {
          document.addEventListener('pointerdown', this.onFreqOutside, true);
          this.freqEl.addEventListener('focusout', (event) => {
            const next = event.relatedTarget;
            if (next && (this.freqEl.contains(next) || (this.subInput && this.subInput === next))) return;
            if (next) this.closeFreq(false);
          });
        }

        this.qtySteps.forEach((btn) => {
          btn.addEventListener('click', () => {
            const { step } = this.qtyLimits();
            this.setQty(this.currentQty() + Number(btn.dataset.stickyQtyStep) * step);
          });
        });
        if (this.qtyInput) {
          this.qtyInput.addEventListener('change', () => this.setQty(parseInt(this.qtyInput.value, 10)));
        }
      }

      // Select a variant the way a shopper would: tick the matching option input
      // in each option group of the page's <variant-selects>, then fire ONE
      // bubbling change from the last group touched. Dawn's variant-selects reads
      // every group's checked/[selected] state on that change, so the page,
      // product-info and (through it) this bar all update from the one event.
      setVariant(id) {
        const variant = this.variants.find((v) => String(v.id) === String(id));
        const selects = this.productInfo.querySelector('variant-selects');
        if (!variant || !selects) return;
        const groups = Array.from(selects.querySelectorAll('fieldset, select')).filter(
          (g) => (g.tagName === 'SELECT' || g.querySelector('input[type="radio"]')) && !g.querySelector('fieldset, select')
        );
        let last = null;
        (variant.options || []).forEach((value, i) => {
          const group = groups[i];
          if (!group) return;
          if (group.tagName === 'SELECT') {
            const option = Array.from(group.options).find((o) => o.value === value);
            if (!option || group.value === value) return;
            Array.from(group.options).forEach((o) => {
              if (o === option) o.setAttribute('selected', 'selected');
              else o.removeAttribute('selected');
            });
            group.value = value;
            last = group;
          } else {
            const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
            if (!radio || radio.checked) return;
            radio.checked = true;
            last = radio;
          }
        });
        if (!last) return;
        // product-info only updates the form's id input when its section fetch
        // returns. Until then the bar must keep showing the shopper's pick, or the
        // sync in between would flick the select back to the old variant.
        this.pendingVariantId = String(variant.id);
        clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(() => {
          this.pendingVariantId = null;
          this.requestSync();
        }, 4000);
        last.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setPlan(mode) {
        const toggle = this.pageToggle();
        if (!toggle || this.pageMode() === mode) return;
        const option = toggle.querySelector(`.magnum-sub-toggle__option[data-mode="${mode}"]`);
        if (option) option.click();
      }

      applyFreq(value) {
        const freq = this.pageFreq();
        if (!freq) return;
        if (this.pageMode() !== 'subscribe') this.setPlan('subscribe');
        if (freq.value !== value) {
          freq.value = value;
          freq.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.requestSync();
      }

      setQty(n) {
        const page = this.pageQty();
        if (!page) return;
        const { min, max } = this.qtyLimits();
        const qty = Math.min(max, Math.max(min, Number.isFinite(n) ? Math.round(n) : min));
        if (String(page.value) !== String(qty)) {
          page.value = String(qty);
          page.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.requestSync();
      }

      freqIsOpen() {
        return !!this.freqEl && !this.freqEl.hidden;
      }

      openFreq(focusChecked) {
        if (!this.freqEl || !this.subInput || !this.pageFreq()) return;
        if (this.pageMode() !== 'subscribe') this.setPlan('subscribe');
        this.freqEl.hidden = false;
        this.freqEl.inert = false;
        this.classList.add('is-freq-open');
        document.addEventListener('keydown', this.onFreqEsc);
        if (focusChecked) {
          const checked = this.freqOpts.find((o) => o.checked) || this.freqOpts[0];
          if (checked) checked.focus({ preventScroll: true });
        }
      }

      closeFreq(returnFocus) {
        if (!this.freqIsOpen()) return;
        const hadFocus = this.freqEl.contains(document.activeElement);
        this.freqEl.hidden = true;
        this.freqEl.inert = true;
        this.classList.remove('is-freq-open');
        document.removeEventListener('keydown', this.onFreqEsc);
        if ((returnFocus || hadFocus) && !this.inert && this.subInput) this.subInput.focus({ preventScroll: true });
      }

      onFreqEsc(event) {
        if (event.key !== 'Escape' || !this.freqIsOpen()) return;
        event.preventDefault();
        this.closeFreq(this.contains(document.activeElement));
      }

      onFreqOutside(event) {
        if (!this.freqIsOpen()) return;
        if (this.freqEl.contains(event.target)) return;
        if (this.subInput && this.subInput.closest('label').contains(event.target)) return;
        this.closeFreq(false);
      }

      publishHeight() {
        const height = Math.round(this.getBoundingClientRect().height);
        if (!height || this.lastHeight === height) return;
        this.lastHeight = height;
        document.documentElement.style.setProperty('--magnum-sticky-atc-h', `${height}px`);
      }

      /*
       * Which control is actually on the page right now.
       *
       * On a sold-out variant Klaviyo's app embed sets the native ATC button to
       * display:none (it stays in the DOM, disabled, reading "Sold out") and
       * injects #klaviyo-bis-button-container with a back-in-stock button. A
       * display:none button reports rect.bottom === 0, so anchoring on it would
       * make `bottom < 0` false forever and the bar would never appear.
       *
       * Returns the element to ANCHOR visibility on and the element to CLICK --
       * they differ for BIS: the real button is a GRANDCHILD inside a
       * hashed-class wrapper div, so `> button` matches nothing.
       */
      buyControl() {
        const bis = document.getElementById('klaviyo-bis-button-container');
        if (bis && bis.isConnected) {
          const rect = bis.getBoundingClientRect();
          if (getComputedStyle(bis).display !== 'none' && rect.height > 0) {
            const target = bis.querySelector('button, a');
            if (target) return { anchor: bis, clickTarget: target, mode: 'bis' };
          }
        }
        const nat = document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.nativeButton;
        if (nat && nat.isConnected) return { anchor: nat, clickTarget: nat, mode: 'atc' };
        return null;
      }

      // Keep the IntersectionObserver pointed at whichever control is live, so a
      // lazy image or accordion that shifts it without a scroll still registers.
      observeAnchor(el) {
        if (!el || this.anchorEl === el || !this.intersectionObserver) return;
        this.anchorEl = el;
        this.intersectionObserver.disconnect();
        this.intersectionObserver.observe(el);
      }

      updateVisibility() {
        const ctl = this.buyControl();
        if (!ctl) return;
        this.observeAnchor(ctl.anchor);
        this.setVisible(ctl.anchor.getBoundingClientRect().bottom < 0);
      }

      onScroll() {
        if (this.visQueued) return;
        this.visQueued = true;
        requestAnimationFrame(() => {
          this.visQueued = false;
          this.updateVisibility();
        });
      }

      setVisible(visible) {
        if (this.isVisible === visible) return;
        this.isVisible = visible;
        this.classList.toggle('is-visible', visible);
        if (!visible) this.closeFreq(false);
        // Keep the bar out of the tab order and the a11y tree while hidden.
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.toggleAttribute('inert', !visible);
        // Hook for the stylesheet to move the accessibility widget out of the
        // bar's way. On <html> so the rule needs no :has() support.
        document.documentElement.classList.toggle('magnum-sticky-atc-visible', visible);
      }

      onDomChange() {
        this.requestSync();
      }

      onPageShow() {
        this.requestSync();
        this.updateVisibility();
      }

      onSectionUnload(event) {
        if (event.detail && String(event.detail.sectionId) === String(this.sectionId)) this.remove();
      }

      requestSync() {
        if (this.syncQueued) return;
        this.syncQueued = true;
        requestAnimationFrame(this.runSync);
      }

      currentVariantId() {
        const form = this.productForm();
        // Scope to the add-to-cart form: the installment form carries its own
        // input[name="id"] and would otherwise win the lookup.
        const input =
          (form && (form.querySelector('input[name="id"]') || form.querySelector('.product-variant-id'))) ||
          this.productInfo.querySelector('.product-variant-id');
        return input ? String(input.value) : null;
      }

      fallbackImageSrc() {
        const items = Array.from(this.productInfo.querySelectorAll('.product__media-item')).filter(
          (item) => getComputedStyle(item).display !== 'none'
        );
        const active = items.find((item) => item.classList.contains('is-active')) || items[0];
        const img = active ? active.querySelector('img') : null;
        return img ? img.getAttribute('src') : null;
      }

      runSync() {
        this.syncQueued = false;
        if (!this.isConnected || !this.productInfo.isConnected) return;

        const variantId = this.currentVariantId();
        const variant = this.variants.find((item) => String(item.id) === variantId) || null;

        // Variant label.
        if (this.variantEl) {
          if (this.hideVariantLine || !variant || variant.title === 'Default Title') {
            this.variantEl.hidden = true;
          } else {
            this.variantEl.hidden = false;
            if (this.variantEl.textContent !== variant.title) this.variantEl.textContent = variant.title;
          }
        }

        // Flavour / size dropdown follows the page, except while a change the bar
        // itself started is still loading (see setVariant).
        if (this.pendingVariantId && variantId === this.pendingVariantId) {
          this.pendingVariantId = null;
          clearTimeout(this.pendingTimer);
        }
        if (this.selectEl && variant && !this.pendingVariantId && this.selectEl.value !== String(variant.id)) {
          this.selectEl.value = String(variant.id);
        }

        // Image.
        if (this.imageEl) {
          const src = (variant && variant.image) || this.fallbackImageSrc() || this.initialImageSrc;
          if (src && this.imageEl.getAttribute('src') !== src) this.imageEl.setAttribute('src', src);
        }

        // ONE-TIME | SUBSCRIBE + frequency follow the page toggle. Only shown when
        // the page really has a subscription toggle with plans.
        const hasToggle = !!this.pageToggle() && !!this.planWrap;
        if (this.planWrap) this.planWrap.hidden = !hasToggle;
        if (hasToggle) {
          const mode = this.pageMode();
          this.planInputs.forEach((input) => {
            const want = input.value === mode;
            if (input.checked !== want) input.checked = want;
          });
          const freq = this.pageFreq();
          const freqValue = freq ? String(freq.value) : null;
          this.freqOpts.forEach((opt) => {
            const want = opt.value === freqValue;
            if (opt.checked !== want) opt.checked = want;
            const priceEl = opt.parentNode.querySelector('[data-sticky-freq-price]');
            if (priceEl) {
              const planPrice = variant && variant.plans ? variant.plans[opt.value] : null;
              const text = planPrice != null ? formatMoney(planPrice, this.moneyFormat) : '';
              if (priceEl.textContent !== text) priceEl.textContent = text;
            }
          });
          if (mode !== 'subscribe') this.closeFreq(false);
        } else {
          this.closeFreq(false);
        }

        // Quantity stepper follows the page quantity input. Hidden when the
        // template has no quantity block.
        const pageQty = this.pageQty();
        if (this.qtyWrap) this.qtyWrap.hidden = !pageQty;
        const qty = this.currentQty();
        if (this.qtyInput && pageQty) {
          const { min, max } = this.qtyLimits();
          if (this.qtyInput.value !== String(qty)) this.qtyInput.value = String(qty);
          this.qtyInput.min = String(min);
          this.qtyInput.max = String(max);
          this.qtySteps.forEach((btn) => {
            btn.disabled = btn.dataset.stickyQtyStep === '-1' ? qty <= min : qty >= max;
          });
        }

        // Price = unit price for the plan the cart will receive x quantity (same
        // rule as the Quattro bar). Hidden whenever the page hides its own price
        // (setUnavailable() on an invalid option combination).
        if (this.priceEl) {
          const livePrice = document.getElementById(`price-${this.sectionId}`);
          const pageHidesPrice = !!livePrice && livePrice.classList.contains('hidden');
          if (variant && !pageHidesPrice) {
            const planId = this.activePlanId();
            const unit = planId && variant.plans && variant.plans[planId] != null ? variant.plans[planId] : variant.price;
            const text = formatMoney(unit * (pageQty ? qty : 1), this.moneyFormat);
            if (this.priceEl.textContent !== text) this.priceEl.textContent = text;
            this.priceEl.hidden = false;
          } else {
            this.priceEl.hidden = true;
          }
        }

        // Availability + label: mirror whichever control is live.
        if (this.buttonEl) {
          const ctl = this.buyControl();
          const mode = ctl ? ctl.mode : 'atc';
          this.dataset.mode = mode;

          if (mode === 'bis') {
            // Back-in-stock: the bar becomes a "Notify me" CTA. Klaviyo owns the
            // label (it is edited in their dashboard, not the theme), so read it
            // off the live button rather than hard-coding a string here.
            const label = (ctl.clickTarget.textContent || '').trim();
            if (this.buttonTextEl && label && this.buttonTextEl.textContent !== label) {
              this.buttonTextEl.textContent = label;
            }
            // Always actionable, and never a spinner: this opens Klaviyo's form,
            // it does not POST to the cart.
            this.buttonEl.removeAttribute('disabled');
            this.buttonEl.removeAttribute('aria-disabled');
            this.buttonEl.classList.remove('loading');
            if (this.spinnerEl) this.spinnerEl.classList.add('hidden');
          } else {
            const disabled = this.nativeButton.hasAttribute('disabled');
            if (this.buttonEl.hasAttribute('disabled') !== disabled) {
              this.buttonEl.toggleAttribute('disabled', disabled);
            }
            const nativeText = this.nativeButton.querySelector('span');
            if (this.buttonTextEl && nativeText && this.buttonTextEl.textContent !== nativeText.textContent) {
              this.buttonTextEl.textContent = nativeText.textContent;
            }

            // Loading state, mirrored rather than timed: it follows the real
            // request, so it clears exactly when the add finishes (or errors) and
            // can never strand a spinner on a failed fetch. Reuses Dawn's own
            // `.button.loading` CSS, which hides the label and centres the spinner.
            const loading = this.nativeButton.classList.contains('loading');
            this.buttonEl.classList.toggle('loading', loading);
            if (loading) {
              this.buttonEl.setAttribute('aria-disabled', 'true');
            } else {
              this.buttonEl.removeAttribute('aria-disabled');
            }
            if (this.spinnerEl) this.spinnerEl.classList.toggle('hidden', !loading);
          }
        }

        // The control can swap between ATC and BIS without a scroll (variant
        // change, or Klaviyo injecting late), so re-evaluate visibility here too.
        this.updateVisibility();
      }

      onClick(event) {
        event.preventDefault();
        // Proxy only. Never reimplement add-to-cart here.
        const ctl = this.buyControl();
        if (!ctl) return;

        // Back-in-stock: forward to Klaviyo's own button so its form opens and
        // its tracking fires, exactly as if the shopper had clicked it directly.
        if (ctl.mode === 'bis') {
          ctl.clickTarget.click();
          return;
        }

        // A typed-but-not-committed quantity (no change event yet) goes to the
        // page input first, so the POST carries what the shopper sees.
        if (this.qtyInput && !this.qtyWrap.hidden && this.qtyInput.value !== String(this.currentQty())) {
          this.setQty(parseInt(this.qtyInput.value, 10));
        }

        const btn = ctl.clickTarget;
        if (btn.hasAttribute('disabled')) return;
        // A submit is already in flight. product-form.js guards this too, but
        // returning here keeps the second click from doing anything at all.
        if (btn.getAttribute('aria-disabled') === 'true') return;
        btn.click();
      }

      disconnectedCallback() {
        // The appendChild to <body> above disconnects and reconnects this node
        // synchronously. Only tear down if it is still detached afterwards.
        Promise.resolve().then(() => {
          if (this.isConnected || this.teardownCalled) return;
          this.teardownCalled = true;
          // Leave no trace: the widget must fall back to its own position.
          // But only if no OTHER bar is mounted. On a theme-editor section
          // reload the incoming bar evicts this one and then sets both of
          // these; this teardown runs a microtask later and would wipe the
          // live bar's state, which its own early-returns never restore.
          if (!document.querySelector('magnum-sticky-atc[data-mounted]')) {
            document.documentElement.classList.remove('magnum-sticky-atc-visible');
            document.documentElement.style.removeProperty('--magnum-sticky-atc-h');
          }
          if (this.resizeObserver) this.resizeObserver.disconnect();
          if (this.intersectionObserver) this.intersectionObserver.disconnect();
          if (this.mutationObserver) this.mutationObserver.disconnect();
          if (this.buttonStateObserver) this.buttonStateObserver.disconnect();
          if (this.productInfo) this.productInfo.removeEventListener('change', this.onDomChange);
          if (this.buttonEl) this.buttonEl.removeEventListener('click', this.onClick);
          if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          window.removeEventListener('pageshow', this.onPageShow);
          document.removeEventListener('shopify:section:unload', this.onSectionUnload);
          document.removeEventListener('pointerdown', this.onFreqOutside, true);
          document.removeEventListener('keydown', this.onFreqEsc);
          clearTimeout(this.pendingTimer);
        });
      }
    }
  );
}
